import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { getFullAPIFileURL } from '../../../common/getFullAPIFileURL';
import type { CoverAiPicture } from '../../../../redux/api/fymAPI.types';
import { useCoverEditor } from '../../CoverEditorContext';
import { coverEditorMessages, pictureFailMessages } from '../../coverEditorMessages';
import { coverAiRuns, earlierCoverPictures, isCoverPictureWorking, isRunFailed, isRunRefused } from './aiPictures';
import { PictureTile } from './PictureTile';
import { usePickPicture } from './usePickPicture';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover editor; each part uses its own slice of it */
import styles from '../../CoverEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The AI pictures of the song (design cover-editor b1, c1, c2): the 4 tiles of the newest run
 * (working, ready, failed) and the ready pictures of the runs before it. A click makes a picture
 * the background.
 */
const AiResultsComponent: React.FC = () => {
  const intl = useIntl();
  const { data, state, readOnly } = useCoverEditor();
  const pick = usePickPicture();
  const runs = useMemo(() => coverAiRuns(data.pictures), [data.pictures]);
  const earlier = useMemo(() => earlierCoverPictures(runs), [runs]);
  const { background } = state.doc;
  const currentUuid =
    background.kind === 'picture' && background.source.kind === 'ai' ? background.source.pictureUuid : null;
  const [latest] = runs;
  if (latest === undefined) {
    return null;
  }

  const tile = (picture: CoverAiPicture) => {
    const isWorking = isCoverPictureWorking(picture);
    let note: string | undefined;
    if (isWorking) {
      note = intl.formatMessage(
        picture.status === 'queued' ? coverEditorMessages.aiQueued : coverEditorMessages.aiPainting
      );
    } else if (picture.status === 'failed') {
      note = intl.formatMessage(coverEditorMessages.aiPictureFailed);
    }
    const { url } = picture;
    return (
      <PictureTile
        key={picture.uuid}
        thumbUrl={picture.thumbUrl === null ? null : getFullAPIFileURL(picture.thumbUrl)}
        label={intl.formatMessage(coverEditorMessages.aiPictureLabel, { number: picture.index + 1 })}
        isOn={picture.uuid === currentUuid}
        isWorking={isWorking}
        note={note}
        disabled={readOnly}
        onPick={
          url === null || picture.status !== 'ready'
            ? undefined
            : () => pick({ kind: 'ai', pictureUuid: picture.uuid }, getFullAPIFileURL(url), picture.palette)
        }
      />
    );
  };

  const failCode = latest.pictures.find((picture) => picture.failCode !== null)?.failCode ?? null;
  return (
    <div className={styles.column}>
      <span className={styles.eyebrow}>
        {intl.formatMessage(latest.isWorking ? coverEditorMessages.aiMaking : coverEditorMessages.aiMade)}
      </span>
      <div className={styles.grid2}>{latest.pictures.map(tile)}</div>
      {isRunFailed(latest) && failCode !== null && (
        <p className={styles.errorText} role="alert">
          {intl.formatMessage(isRunRefused(latest) ? pictureFailMessages.IMAGE_REFUSED : pictureFailMessages[failCode])}{' '}
          {intl.formatMessage(coverEditorMessages.aiNotCounted)}
        </p>
      )}
      {earlier.length > 0 && (
        <>
          <span className={styles.eyebrow}>{intl.formatMessage(coverEditorMessages.aiEarlier)}</span>
          <div className={styles.grid4}>{earlier.map(tile)}</div>
        </>
      )}
    </div>
  );
};

export const AiResults = TypedMemo(AiResultsComponent);
